import { useCurrentOrg, useCurrentUser, useSessionTokenGetter } from '@develio/auth/client';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Surface } from '../components/Surface';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';
const ONBOARDING_DISMISSED_KEY = 'develio:onboarding-dismissed';

function markOnboardingDismissed(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ONBOARDING_DISMISSED_KEY, '1');
  } catch {
    // storage full/disabled — non-fatal
  }
}

/**
 * First-run org setup — one form, one POST, then straight to the wizard.
 *
 * The user types city + state (+ optional ZIP and org name); the backend
 * resolves county via the Census Geocoder and populates the org's default
 * Jurisdiction Profile with state-level estimated rates. Methodology and
 * RIMS already work out of the box (sample profile seeded on signup; national
 * RIMS fallback covers trial orgs), so submitting this form is enough to run
 * a first analysis end-to-end.
 *
 * Rates are placeholders — the profile stays marked `isSample` so the
 * dashboard banner keeps nudging the owner to refine them.
 */
const US_STATES: ReadonlyArray<{ abbr: string; name: string }> = [
  { abbr: 'AL', name: 'Alabama' },
  { abbr: 'AK', name: 'Alaska' },
  { abbr: 'AZ', name: 'Arizona' },
  { abbr: 'AR', name: 'Arkansas' },
  { abbr: 'CA', name: 'California' },
  { abbr: 'CO', name: 'Colorado' },
  { abbr: 'CT', name: 'Connecticut' },
  { abbr: 'DE', name: 'Delaware' },
  { abbr: 'FL', name: 'Florida' },
  { abbr: 'GA', name: 'Georgia' },
  { abbr: 'HI', name: 'Hawaii' },
  { abbr: 'ID', name: 'Idaho' },
  { abbr: 'IL', name: 'Illinois' },
  { abbr: 'IN', name: 'Indiana' },
  { abbr: 'IA', name: 'Iowa' },
  { abbr: 'KS', name: 'Kansas' },
  { abbr: 'KY', name: 'Kentucky' },
  { abbr: 'LA', name: 'Louisiana' },
  { abbr: 'ME', name: 'Maine' },
  { abbr: 'MD', name: 'Maryland' },
  { abbr: 'MA', name: 'Massachusetts' },
  { abbr: 'MI', name: 'Michigan' },
  { abbr: 'MN', name: 'Minnesota' },
  { abbr: 'MS', name: 'Mississippi' },
  { abbr: 'MO', name: 'Missouri' },
  { abbr: 'MT', name: 'Montana' },
  { abbr: 'NE', name: 'Nebraska' },
  { abbr: 'NV', name: 'Nevada' },
  { abbr: 'NH', name: 'New Hampshire' },
  { abbr: 'NJ', name: 'New Jersey' },
  { abbr: 'NM', name: 'New Mexico' },
  { abbr: 'NY', name: 'New York' },
  { abbr: 'NC', name: 'North Carolina' },
  { abbr: 'ND', name: 'North Dakota' },
  { abbr: 'OH', name: 'Ohio' },
  { abbr: 'OK', name: 'Oklahoma' },
  { abbr: 'OR', name: 'Oregon' },
  { abbr: 'PA', name: 'Pennsylvania' },
  { abbr: 'RI', name: 'Rhode Island' },
  { abbr: 'SC', name: 'South Carolina' },
  { abbr: 'SD', name: 'South Dakota' },
  { abbr: 'TN', name: 'Tennessee' },
  { abbr: 'TX', name: 'Texas' },
  { abbr: 'UT', name: 'Utah' },
  { abbr: 'VT', name: 'Vermont' },
  { abbr: 'VA', name: 'Virginia' },
  { abbr: 'WA', name: 'Washington' },
  { abbr: 'WV', name: 'West Virginia' },
  { abbr: 'WI', name: 'Wisconsin' },
  { abbr: 'WY', name: 'Wyoming' },
];

export function OnboardingRoute() {
  const user = useCurrentUser();
  const org = useCurrentOrg();
  const getToken = useSessionTokenGetter();
  const navigate = useNavigate();

  const [orgName, setOrgName] = useState(org?.name ?? '');
  const [city, setCity] = useState('');
  const [state, setState] = useState('TX');
  const [zip, setZip] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<{
    city: string;
    countyName: string;
    stateName: string;
    geocoded: boolean;
  } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!city.trim() || state.length !== 2) {
      setError('Please enter a city and select a state.');
      return;
    }
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/onboarding/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orgName: orgName.trim() || undefined,
          city: city.trim(),
          state,
          zip: zip.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      const data = (await res.json()) as {
        resolved: { city: string; countyName: string; stateName: string; geocoded: boolean };
      };
      setResolved(data.resolved);
      markOnboardingDismissed();
      // Brief pause so the user sees the success state before we redirect.
      setTimeout(() => navigate('/analyses/new'), 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-6">
      <div>
        <h1 className="font-display text-3xl text-slate-900">
          Welcome{user?.fullName ? `, ${user.fullName.split(' ')[0]}` : ''}.
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Tell us where your organization works. We'll prefill your jurisdiction with state-level
          estimated tax rates and national RIMS II multipliers so you can run your first analysis in
          a couple of minutes. You can replace any value later in <strong>Organization</strong>{' '}
          &rarr; <strong>Jurisdiction</strong>.
        </p>
      </div>

      <Surface>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label
              htmlFor="onb-org-name"
              className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600"
            >
              Organization name
            </label>
            <input
              id="onb-org-name"
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="e.g. Canyon Economic Development Corporation"
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-teal-500 focus:outline-none focus:ring-1 focus:ring-brand-teal-500"
            />
          </div>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-7">
              <label
                htmlFor="onb-city"
                className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600"
              >
                Primary city <span className="text-red-500">*</span>
              </label>
              <input
                id="onb-city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g. Canyon"
                required
                className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-teal-500 focus:outline-none focus:ring-1 focus:ring-brand-teal-500"
              />
            </div>
            <div className="col-span-3">
              <label
                htmlFor="onb-state"
                className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600"
              >
                State <span className="text-red-500">*</span>
              </label>
              <select
                id="onb-state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-teal-500 focus:outline-none focus:ring-1 focus:ring-brand-teal-500"
              >
                {US_STATES.map((s) => (
                  <option key={s.abbr} value={s.abbr}>
                    {s.abbr}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label
                htmlFor="onb-zip"
                className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600"
              >
                ZIP
              </label>
              <input
                id="onb-zip"
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                placeholder="79015"
                className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-teal-500 focus:outline-none focus:ring-1 focus:ring-brand-teal-500"
              />
            </div>
          </div>

          <div className="rounded-md bg-slate-50 px-4 py-3 text-xs text-slate-600">
            <div className="font-medium text-slate-700">What this does:</div>
            <ul className="mt-1 list-inside list-disc space-y-0.5">
              <li>Looks up your county via the US Census Geocoder</li>
              <li>Sets state-level estimated tax + cost-of-services rates</li>
              <li>Uses national RIMS II multipliers as your default</li>
              <li>Keeps a "sample data" flag on so reports stay clearly marked until you refine</li>
            </ul>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {resolved && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              All set — {resolved.city}, {resolved.countyName}, {resolved.stateName}.
              {resolved.geocoded ? ' County resolved via Census Geocoder. ' : ' '}
              Redirecting to the analysis wizard…
            </div>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={() => {
                markOnboardingDismissed();
                navigate('/dashboard');
              }}
              className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
            >
              Skip for now
            </button>
            <button
              type="submit"
              disabled={submitting || !!resolved}
              className="rounded-md bg-brand-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && !resolved ? 'Setting up your org…' : 'Finish setup → New analysis'}
            </button>
          </div>
        </form>
      </Surface>

      <p className="text-center text-xs text-slate-400">
        Refine your jurisdiction, methodology, scoring rubric, and RIMS II data any time from the{' '}
        <strong>Organization</strong> menu in the sidebar.
      </p>
    </div>
  );
}
